import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from '../Breadcrumb';

interface EvaluationProps {
  projectId: string;
}

interface EvaluationRun {
  timestamp: string;
  runId: string;
  pipelineVersion: string;
  dataSize: number;
  status: string;
  accuracy: number;
  recall: number;
  precision: number;
  f1: number;
}

const Evaluation: React.FC<EvaluationProps> = ({ projectId }) => {
  const [evaluationRuns, setEvaluationRuns] = useState<EvaluationRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState('');
  const [dataMode, setDataMode] = useState('full');
  const [samplePercentage, setSamplePercentage] = useState(10);
  const [pipelineVersions, setPipelineVersions] = useState<{version: string, name: string, display: string}[]>([]);
  const [totalDataPoints, setTotalDataPoints] = useState(0);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  
  useEffect(() => {
    fetchVersions();
    fetchDatasetSummary();
    fetchEvaluationRuns();
  }, [projectId]);
  
  useEffect(() => {
    // Check if latest run is pending and set up auto-refresh
    const latestRun = evaluationRuns.length > 0 ? evaluationRuns[0] : null;
    
    if (latestRun && latestRun.status === 'pending') {
      if (!refreshInterval) {
        const interval = setInterval(() => {
          fetchEvaluationRuns();
        }, 30000);
        setRefreshInterval(interval);
      }
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
    
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [evaluationRuns, refreshInterval]);
  
  const fetchEvaluationRuns = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/evaluation/runs`, {
        headers: {
          'Authorization': token || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const sortedRuns = data.runs.sort((a: EvaluationRun, b: EvaluationRun) => 
          new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        );
        setEvaluationRuns(sortedRuns);
      }
    } catch (error) {
      console.error('Error fetching evaluation runs:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchVersions = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/versions`, {
        headers: {
          'Authorization': token || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPipelineVersions(data.versions);
        if (data.versions.length > 0) {
          setSelectedVersion(data.versions[0].version);
        }
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  };
  
  const fetchDatasetSummary = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/dataset/summary`, {
        headers: {
          'Authorization': token || ''
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTotalDataPoints(data.total_rows || 0);
      }
    } catch (error) {
      console.error('Error fetching dataset summary:', error);
    }
  };
  
  const getSampleSize = () => {
    return Math.ceil((totalDataPoints * samplePercentage) / 100);
  };
  
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem;
      background: ${type === 'success' ? '#d4edda' : '#f8d7da'};
      color: ${type === 'success' ? '#155724' : '#721c24'};
      border-radius: 4px;
      z-index: 2000;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 5000);
  };

  return (
    <div className="module-content">
      <Breadcrumb items={[
        { label: 'Home', path: '/' },
        { label: 'Projects', path: '/' },
        { label: projectId, path: `/project/${projectId}` },
        { label: 'Test', path: `/project/${projectId}/test` },
        { label: 'Evaluation' }
      ]} />
      <h2>Model Evaluation</h2>
      <button 
        style={{ 
          backgroundColor: '#007bff', 
          color: 'white', 
          border: 'none', 
          padding: '0.5rem 1rem', 
          borderRadius: '4px', 
          cursor: 'pointer',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
        onClick={() => setShowModal(true)}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
        </svg>
        Create Evaluation Run
      </button>
      <div className="evaluation-table">
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Timestamp</th>
              <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Run ID</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Pipeline Version</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Data Size</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Status</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Accuracy</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Recall</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Precision</th>
              <th style={{ padding: '0.75rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>F1</th>
            </tr>
          </thead>
          <tbody>
            {evaluationRuns.map((run, index) => (
              <tr 
                key={run.runId} 
                style={{ 
                  backgroundColor: index % 2 === 0 ? '#fff' : '#f9f9f9',
                  cursor: 'pointer'
                }}
                onClick={() => window.location.href = `/project/${projectId}/test/evaluation/${run.runId}`}
              >
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>{new Date(run.timestamp).toLocaleString()}</td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee' }}>{run.runId}</td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'center' }}>{run.pipelineVersion}</td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'center' }}>{run.dataSize.toLocaleString()}</td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'center' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    backgroundColor: 
                      run.status === 'completed' ? '#d4edda' :
                      run.status === 'running' ? '#fff3cd' :
                      run.status === 'failed' ? '#f8d7da' : '#e2e3e5',
                    color:
                      run.status === 'completed' ? '#155724' :
                      run.status === 'running' ? '#856404' :
                      run.status === 'failed' ? '#721c24' : '#495057'
                  }}>
                    {run.status.toUpperCase()}
                  </span>
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'center' }}>{run.accuracy !== null && run.accuracy !== undefined ? (run.accuracy * 100).toFixed(1) + '%' : '-'}</td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'center' }}>{run.recall !== null && run.recall !== undefined ? (run.recall * 100).toFixed(1) + '%' : '-'}</td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'center' }}>{run.precision !== null && run.precision !== undefined ? (run.precision * 100).toFixed(1) + '%' : '-'}</td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #eee', textAlign: 'center' }}>{run.f1 !== null && run.f1 !== undefined ? (run.f1 * 100).toFixed(1) + '%' : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {loading && (
        <p style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>Loading evaluation results...</p>
      )}
      
      {!loading && evaluationRuns.length === 0 && (
        <p style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>No evaluation runs found.</p>
      )}
      
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '400px',
            maxWidth: '90%'
          }}>
            <h3 style={{ marginTop: 0 }}>Create Evaluation Run</h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Pipeline Version:</label>
              <select 
                value={selectedVersion} 
                onChange={(e) => setSelectedVersion(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
              >
                {pipelineVersions.map(version => (
                  <option key={version.version} value={version.version}>
                    {version.display}
                  </option>
                ))}
              </select>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Data Size:</label>
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input 
                    type="radio" 
                    name="dataMode" 
                    value="full" 
                    checked={dataMode === 'full'}
                    onChange={(e) => setDataMode(e.target.value)}
                  />
                  Full set ({totalDataPoints.toLocaleString()} data points)
                </label>
              </div>
              <div style={{ marginBottom: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input 
                    type="radio" 
                    name="dataMode" 
                    value="sample" 
                    checked={dataMode === 'sample'}
                    onChange={(e) => setDataMode(e.target.value)}
                  />
                  Sample
                </label>
                {dataMode === 'sample' && (
                  <input 
                    type="number" 
                    value={samplePercentage}
                    onChange={(e) => setSamplePercentage(parseInt(e.target.value) || 0)}
                    min="1"
                    max="100"
                    style={{ 
                      marginLeft: '1.5rem', 
                      marginTop: '0.5rem',
                      width: '80px', 
                      padding: '0.25rem', 
                      border: '1px solid #ccc', 
                      borderRadius: '4px' 
                    }}
                  />
                )}
                {dataMode === 'sample' && (
                  <span style={{ marginLeft: '0.5rem' }}>% ({getSampleSize().toLocaleString()} data points)</span>
                )}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' }}>
              <button 
                onClick={() => setShowModal(false)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #ccc',
                  backgroundColor: 'white',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={async () => {
                  setIsRunning(true);
                  try {
                    const session = await fetchAuthSession();
                    const token = session.tokens?.idToken?.toString();
                    
                    const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/${projectId}/evaluation/run`, {
                      method: 'POST',
                      headers: {
                        'Authorization': token || '',
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({
                        pipeline_version: selectedVersion,
                        data_mode: dataMode,
                        sample_percentage: samplePercentage
                      })
                    });
                    
                    if (response.ok) {
                      const result = await response.json();
                      setShowModal(false);
                      showToast(`Eval run created: ${result.evaluation_run_id}`, 'success');
                      
                      // Wait 3 seconds then refresh the table
                      setTimeout(() => {
                        fetchEvaluationRuns();
                      }, 3000);
                    } else {
                      showToast('Failed to create evaluation run', 'error');
                    }
                  } catch (error) {
                    showToast('Error creating evaluation run', 'error');
                  } finally {
                    setIsRunning(false);
                  }
                }}
                disabled={isRunning}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  backgroundColor: isRunning ? '#6c757d' : '#007bff',
                  color: 'white',
                  borderRadius: '4px',
                  cursor: isRunning ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {isRunning && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z"/>
                  </svg>
                )}
                {isRunning ? 'Running...' : 'Run'}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Evaluation;